import React, { useState } from 'react';
import { XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';
import { testAutomationRule } from '../services/automationApi';
import { TaskAutomationRule } from '../types/Automation';

interface TaskAutomationTestModalProps {
  rule: TaskAutomationRule;
  onClose: () => void;
}

const TaskAutomationTestModal: React.FC<TaskAutomationTestModalProps> = ({
  rule,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const testLeadData = {
        id: 999, // Mock lead ID for testing
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com'
      };

      const result = await testAutomationRule(rule, testLeadData);
      setTestResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to test automation rule');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Test Automation Rule
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Rule Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Rule Configuration</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Task Name:</span> {rule.taskName}</p>
              <p><span className="font-medium">Trigger Stage:</span> {rule.stageName}</p>
              <p><span className="font-medium">Priority:</span> {rule.priority}</p>
              <p><span className="font-medium">Category:</span> {rule.categoryName || 'Not set'}</p>
              <p><span className="font-medium">Due Date:</span> {rule.dueDateOffset} days after trigger</p>
              {rule.reminderOffset && (
                <p><span className="font-medium">Reminder:</span> {rule.reminderOffset} hours before due date</p>
              )}
            </div>
          </div>

          {/* Test Button */}
          <div className="mb-6">
            <button
              onClick={handleTest}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              {loading ? 'Testing...' : 'Test Rule'}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <h3 className="font-medium text-green-900 mb-3">Test Result</h3>
              <div className="text-sm text-green-800 space-y-2">
                <p><span className="font-medium">Task Title:</span> {testResult.title}</p>
                <p><span className="font-medium">Lead:</span> {testResult.leadName} ({testResult.leadEmail})</p>
                <p><span className="font-medium">Description:</span> {testResult.description || 'No description'}</p>
                <p><span className="font-medium">Category:</span> {testResult.category || 'Not set'}</p>
                <p><span className="font-medium">Priority:</span> {testResult.priority}</p>
                <p><span className="font-medium">Start Date:</span> {formatDate(testResult.startDate)}</p>
                <p><span className="font-medium">Due Date:</span> {formatDate(testResult.dueDate)}</p>
                {testResult.reminderDate && (
                  <p><span className="font-medium">Reminder:</span> {formatDateTime(testResult.reminderDate)}</p>
                )}
                <p><span className="font-medium">Assigned To:</span> {testResult.assignedTo || 'Not assigned'}</p>
                <p><span className="font-medium">Supervisor:</span> {testResult.supervisor || 'Not assigned'}</p>
              </div>
              
              <div className="mt-4 p-3 bg-green-100 rounded">
                <p className="text-sm text-green-800">
                  <span className="font-medium">Note:</span> This is a preview of what would be created. 
                  The actual task will be created when a real lead moves to the "{rule.stageName}" stage.
                </p>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskAutomationTestModal;
